import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  LinearProgress,
  Paper,
  Stack,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import { EmojiEvents as TrophyIcon, Launch as LaunchIcon } from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import { formatDateSafe } from '../../../shared/utils/dateHelpers';
import { NominationRole, SuccessNominationRecord } from '../nomination.types';
import { excerpt } from '../utils/nominationFilters';

export interface ClientNominationsSummaryProps {
  nominations: SuccessNominationRecord[];
  isLoading: boolean;
  error: string | null;
  scopeRole: NominationRole;
  /** Reviewer-only link to the Success Nominations page, pre-filtered to this client. */
  onViewAll?: () => void;
}

/**
 * Compact strip on the Client Success tab summarising prior nominations so staff
 * can see at a glance whether (and when) a client has already been put forward.
 */
export const ClientNominationsSummary: React.FC<ClientNominationsSummaryProps> = ({
  nominations,
  isLoading,
  error,
  scopeRole,
  onViewAll,
}) => {
  const theme = useTheme();
  const isReviewer = scopeRole === 'ADMIN' || scopeRole === 'MANAGER';
  const latest = nominations[0];
  const title = isReviewer ? 'Success nominations' : 'Your nominations for this client';

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 2,
        border: '1px solid',
        borderColor: nominations.length > 0 ? alpha(theme.palette.warning.main, 0.4) : 'divider',
        backgroundColor: nominations.length > 0 ? alpha(theme.palette.warning.main, 0.04) : 'background.paper',
      }}
    >
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            backgroundColor: alpha(theme.palette.warning.main, 0.12),
            color: theme.palette.warning.dark,
          }}
        >
          <TrophyIcon fontSize="small" />
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="subtitle2" fontWeight={600}>
              {title}
            </Typography>
            {nominations.length > 0 && (
              <Chip label={`${nominations.length} submitted`} size="small" color="warning" variant="outlined" />
            )}
          </Stack>

          {isLoading ? (
            <LinearProgress sx={{ mt: 1, maxWidth: 240 }} />
          ) : error ? (
            <Alert severity="warning" sx={{ mt: 1, py: 0 }}>
              {error}
            </Alert>
          ) : latest ? (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Latest {formatDistanceToNow(latest.created_at, { addSuffix: true })} by {latest.nominated_by_name}
                {latest.reason ? ` - “${excerpt(latest.reason, 140)}”` : ''}
              </Typography>
              {nominations.length > 1 && (
                <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
                  {nominations.slice(1, 4).map(nomination => (
                    <Tooltip key={nomination.id} title={excerpt(nomination.reason, 200) || 'No reason recorded'}>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`${formatDateSafe(nomination.created_at, 'd MMM yyyy')} · ${nomination.nominated_by_name}`}
                      />
                    </Tooltip>
                  ))}
                  {nominations.length > 4 && (
                    <Chip size="small" variant="outlined" label={`+${nominations.length - 4} more`} />
                  )}
                </Stack>
              )}
            </>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              No nominations yet. Put this client forward when they hit a milestone worth celebrating.
            </Typography>
          )}
        </Box>

        {isReviewer && onViewAll && nominations.length > 0 && (
          <Button size="small" variant="text" endIcon={<LaunchIcon />} onClick={onViewAll} sx={{ flexShrink: 0 }}>
            Review all
          </Button>
        )}
      </Stack>
    </Paper>
  );
};

export default ClientNominationsSummary;
